import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import templeService from '../services/templeService'
import { Spinner } from "../components/Spinner"
import Navbar from '../components/Navbar'

export default function HomePage() {
  // State for the hero image carousel
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const heroImages = [
    "https://ourtemples.info/wp-content/uploads/2023/12/hero1.webp",
    "https://ourtemples.info/wp-content/uploads/2023/12/hero2.webp",
    "https://ourtemples.info/wp-content/uploads/2023/12/hero3.webp",
  ]

  // State for featured temples
  const [featuredTemples, setFeaturedTemples] = useState([])
  const [featuredIndex, setFeaturedIndex] = useState(0)
  const [featuredLoading, setFeaturedLoading] = useState(true)
  const [featuredError, setFeaturedError] = useState(null)

  // State for temple categories
  const [templeCategories, setTempleCategories] = useState([])
  const [categoriesLoading, setCategoriesLoading] = useState(true)
  const [categoriesError, setCategoriesError] = useState(null)

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Fetch featured temples
  useEffect(() => {
    const fetchFeaturedTemples = async () => {
      try {
        setFeaturedLoading(true) 
        const data = await templeService.getFeaturedTemples(8)

        // If no featured temples, use default data
        if (!data || data.length === 0) {
          setFeaturedTemples([
            {
              name: "Kedarnath Temple",
              location: { city: "Kedarnath", state: "Uttarakhand" },
              mainImage: "/placeholder.svg?height=400&width=600",
              description: "One of the twelve Jyotirlingas of Lord Shiva, located in the Himalayas.",
              _id: "default1",
            },
            {
              name: "Golden Temple",
              location: { city: "Amritsar", state: "Punjab" },
              mainImage: "/placeholder.svg?height=400&width=600",
              description: "The holiest Gurdwara and an important pilgrimage site of Sikhism.",
              _id: "default2",
            },
            {
              name: "Meenakshi Temple",
              location: { city: "Madurai", state: "Tamil Nadu" },
              mainImage: "/placeholder.svg?height=400&width=600",
              description: "A historic Hindu temple dedicated to Goddess Meenakshi and Lord Sundareswarar.",
              _id: "default3",
            },
            {
              name: "Jagannath Temple",
              location: { city: "Puri", state: "Odisha" },
              mainImage: "/placeholder.svg?height=400&width=600",
              description: "One of the Char Dham pilgrimage sites dedicated to Lord Jagannath.",
              _id: "default4",
            },
          ])
        } else {
          setFeaturedTemples(data)
        }

        setFeaturedLoading(false)
      } catch (error) {
        console.error("Error fetching featured temples:", error)
        setFeaturedError("Failed to load featured temples")
        setFeaturedLoading(false)
      }
    }

    fetchFeaturedTemples()
  }, [])

  // Fetch temples by category
  useEffect(() => {
    const fetchTempleCategories = async () => {
      try {
        setCategoriesLoading(true)

        // Define the categories we want to fetch
        const categories = ["Shiva", "Vishnu", "Devi", "Ganesha"]

        // Fetch temples for each category
        const categoryPromises = categories.map(async (deity) => {
          try {
            const temples = await templeService.getTemplesByCategory(deity, 4)
            return {
              deity,
              temples: temples.length > 0 ? temples : getDefaultTemplesForCategory(deity),
            }
          } catch (error) {
            console.error(`Error fetching ${deity} temples:`, error)
            return {
              deity,
              temples: getDefaultTemplesForCategory(deity),
            }
          }
        })

        const results = await Promise.all(categoryPromises)
        setTempleCategories(results)
        setCategoriesLoading(false)
      } catch (error) {
        console.error("Error fetching temple categories:", error)
        setCategoriesError("Failed to load temple categories")
        setCategoriesLoading(false)

        // Set default categories if there's an error
        setTempleCategories([
          {
            deity: "Shiva",
            temples: getDefaultTemplesForCategory("Shiva"),
          },
          {
            deity: "Vishnu",
            temples: getDefaultTemplesForCategory("Vishnu"),
          },
          {
            deity: "Devi",
            temples: getDefaultTemplesForCategory("Devi"),
          },
          {
            deity: "Ganesha",
            temples: getDefaultTemplesForCategory("Ganesha"),
          },
        ])
      }
    }

    fetchTempleCategories()
  }, [])

  // Helper function to get default temples for a category
  const getDefaultTemplesForCategory = (deity) => {
    const defaultTemplates = {
      Shiva: [
        { name: "Kedarnath", mainImage: "/placeholder.svg?height=300&width=400", _id: "s1" },
        { name: "Kashi Vishwanath", mainImage: "/placeholder.svg?height=300&width=400", _id: "s2" },
        { name: "Somnath", mainImage: "/placeholder.svg?height=300&width=400", _id: "s3" },
        { name: "Rameshwaram", mainImage: "/placeholder.svg?height=300&width=400", _id: "s4" },
      ],
      Vishnu: [
        { name: "Tirupati Balaji", mainImage: "/placeholder.svg?height=300&width=400", _id: "v1" },
        { name: "Badrinath", mainImage: "/placeholder.svg?height=300&width=400", _id: "v2" },
        { name: "Srirangam", mainImage: "/placeholder.svg?height=300&width=400", _id: "v3" },
        { name: "Dwarka", mainImage: "/placeholder.svg?height=300&width=400", _id: "v4" },
      ],
      Devi: [
        { name: "Vaishno Devi", mainImage: "/placeholder.svg?height=300&width=400", _id: "d1" },
        { name: "Kamakhya", mainImage: "/placeholder.svg?height=300&width=400", _id: "d2" },
        { name: "Meenakshi", mainImage: "/placeholder.svg?height=300&width=400", _id: "d3" },
        { name: "Chamundeshwari", mainImage: "/placeholder.svg?height=300&width=400", _id: "d4" },
      ],
      Ganesha: [
        { name: "Siddhivinayak", mainImage: "/placeholder.svg?height=300&width=400", _id: "g1" },
        { name: "Dagdusheth Halwai", mainImage: "/placeholder.svg?height=300&width=400", _id: "g2" },
        { name: "Kanipakam", mainImage: "/placeholder.svg?height=300&width=400", _id: "g3" },
        { name: "Ranjangaon", mainImage: "/placeholder.svg?height=300&width=400", _id: "g4" },
      ],
    }

    return defaultTemplates[deity] || []
  }

  // Auto-rotate hero images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  // Auto-rotate featured temples
  useEffect(() => {
    if (featuredTemples.length > 0) {
      const interval = setInterval(() => {
        setFeaturedIndex((prevIndex) => (prevIndex + 1) % featuredTemples.length)
      }, 7000)
      return () => clearInterval(interval)
    }
  }, [featuredTemples])

  return (
    <div className="min-h-screen bg-gradient-to-b from-divine-50 via-white to-sacred-50">
      {/* Navigation */}
      <Navbar />

      {/* Hero Section with Image Carousel */}
      <section className="relative h-[80vh] overflow-hidden">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImageIndex ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url(${image})` }}>
              <div className="absolute inset-0 bg-black bg-opacity-40"></div>
            </div>
          </div>
        ))}

        <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-4">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 drop-shadow-lg">Discover India's Divine Heritage</h1>
          <p className="text-xl md:text-2xl max-w-3xl mb-8 drop-shadow-md">
            Explore the spiritual and architectural marvels of ancient temples across India
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/temple">
              <button className="bg-spiritual-600 hover:bg-spiritual-700 text-white px-8 py-3 rounded-full text-lg font-semibold transition-colors shadow-lg">
                Explore Temples
              </button>
            </Link>
            {/* <Link href="#pilgrimage">
              <button className="bg-transparent hover:bg-white/20 text-white border-2 border-white px-8 py-3 rounded-full text-lg font-semibold transition-colors shadow-lg">
                Plan Pilgrimage
              </button>
            </Link> */}
          </div>
        </div>

        {/* Carousel Indicators */}
        <div className="absolute bottom-6 left-0 right-0 flex justify-center space-x-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-3 h-3 rounded-full ${index === currentImageIndex ? "bg-white" : "bg-white bg-opacity-50"}`}
            />
          ))}
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-divine-50 to-white" id="about">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-spiritual-700 mb-4">India's Sacred Temples</h2>
            <div className="w-24 h-1 bg-spiritual-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              India is home to thousands of temples that showcase the country's rich cultural and spiritual heritage.
              These architectural marvels stand as a testament to India's ancient civilization and deep-rooted religious
              traditions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-300">
              <div className="h-48 bg-sacred-100 flex items-center justify-center">
                <span className="text-6xl text-sacred-500">🕉️</span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-spiritual-700 mb-3">Spiritual Significance</h3>
                <p className="text-gray-600">
                  Temples in India are not just places of worship but centers of spiritual energy that connect devotees
                  with the divine.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-300">
              <div className="h-48 bg-royal-100 flex items-center justify-center">
                <span className="text-6xl text-royal-500">🏛️</span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-spiritual-700 mb-3">Architectural Marvels</h3>
                <p className="text-gray-600">
                  From intricate carvings to massive structures, Indian temples showcase extraordinary architectural
                  skills and artistic excellence.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-300">
              <div className="h-48 bg-divine-100 flex items-center justify-center">
                <span className="text-6xl text-divine-500">🪔</span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-spiritual-700 mb-3">Cultural Heritage</h3>
                <p className="text-gray-600">
                  Temples have been centers of art, music, dance, and education, preserving and promoting India's rich
                  cultural traditions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Temple Section with Carousel */}
      <section className="py-16 px-4 bg-gradient-to-br from-sacred-50 to-sacred-100" id="temples">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-sacred-700 mb-4">Featured Temples</h2>
            <div className="w-24 h-1 bg-sacred-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Discover some of the most magnificent and spiritually significant temples across India
            </p>
          </div>

          {featuredLoading ? (
            <div className="flex justify-center items-center h-64">
              <Spinner />
            </div>
          ) : featuredError ? (
            <div className="text-center text-red-500 p-4 bg-red-50 rounded-lg">{featuredError}</div>
          ) : featuredTemples.length > 0 ? (
            <div className="relative mt-12 bg-white rounded-xl shadow-xl overflow-hidden">
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2">
                  <img
                    src={featuredTemples[featuredIndex]?.mainImage || "/placeholder.svg?height=400&width=600"}
                    alt={featuredTemples[featuredIndex]?.name}
                    className="w-full h-64 md:h-full object-cover"
                  />
                </div>
                <div className="md:w-1/2 p-8 flex flex-col justify-center">
                  <h3 className="text-3xl font-bold text-sacred-700 mb-2">{featuredTemples[featuredIndex]?.name}</h3>
                  <p className="text-sacred-600 mb-4">
                    {featuredTemples[featuredIndex]?.location?.city}, {featuredTemples[featuredIndex]?.location?.state}
                  </p>
                  <p className="text-gray-600 mb-6">{featuredTemples[featuredIndex]?.description}</p>
                  <Link to={`/temple/${featuredTemples[featuredIndex]?._id}`}>
                    <button className="self-start bg-sacred-600 hover:bg-sacred-700 text-white px-6 py-2 rounded-lg text-lg font-semibold transition-colors">
                      Learn More
                    </button>
                  </Link>
                </div>
              </div>

              {/* Carousel Controls */}
              <div className="absolute bottom-4 right-4 flex space-x-2">
                {featuredTemples.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setFeaturedIndex(index)}
                    className={`w-3 h-3 rounded-full ${index === featuredIndex ? "bg-sacred-600" : "bg-sacred-300"}`}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 p-4">No featured temples available at the moment.</div>
          )}
        </div>
      </section>

      {/* Temples by Deity Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-royal-50 to-white" id="deities">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-royal-700 mb-4">Temples by Deity</h2>
            <div className="w-24 h-1 bg-royal-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Explore temples dedicated to different Hindu deities across India
            </p>
          </div>

          {categoriesLoading ? (
            <div className="flex justify-center items-center h-64">
              <Spinner />
            </div>
          ) : categoriesError ? (
            <div className="text-center text-red-500 p-4 bg-red-50 rounded-lg">{categoriesError}</div>
          ) : (
            <div className="mt-12">
              {templeCategories.map((category, categoryIndex) => (
                <div key={categoryIndex} className="mb-16">
                  <h3 className="text-2xl font-bold text-royal-700 mb-6 flex items-center">
                    <span className="w-8 h-8 bg-royal-600 rounded-full text-white flex items-center justify-center mr-3">
                      {categoryIndex + 1}
                    </span>
                    Temples of {category.deity}
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {category.temples.map((temple, templeIndex) => (
                      <div
                        key={temple._id || templeIndex}
                        className="bg-white rounded-lg shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-300"
                      >
                        <img
                          src={temple.mainImage || "/placeholder.svg?height=300&width=400"}
                          alt={temple.name}
                          className="w-full h-48 object-cover"
                        />
                        <div className="p-4">
                          <h4 className="text-xl font-semibold text-royal-700 mb-2">{temple.name}</h4>
                          <Link to={`/temple/${temple._id}`}>
                            <button className="text-royal-600 hover:text-royal-800 font-medium flex items-center">
                              View Details
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5 ml-1"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pilgrimage Routes Section */}
      {/* <section className="py-16 px-4 bg-gradient-to-br from-nature-50 to-nature-100" id="pilgrimage">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-nature-700 mb-4">Sacred Pilgrimage Routes</h2>
            <div className="w-24 h-1 bg-nature-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Discover the most revered pilgrimage circuits that have been followed by devotees for centuries
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-6">
                <h3 className="text-2xl font-bold text-nature-700 mb-3">Char Dham</h3>
                <p className="text-gray-600 mb-4">
                  The four sacred sites of Yamunotri, Gangotri, Kedarnath, and Badrinath in the Himalayas form the Char
                  Dham pilgrimage.
                </p>
                <div className="flex justify-between items-center mt-4">
                  <span className="text-nature-600 font-medium">4 Temples</span>
                  <button className="bg-nature-600 hover:bg-nature-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
                    Explore Route
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-6">
                <h3 className="text-2xl font-bold text-nature-700 mb-3">Dwadash Jyotirlinga</h3>
                <p className="text-gray-600 mb-4">
                  The twelve sacred shrines of Lord Shiva spread across India, each housing a Jyotirlinga.
                </p>
                <div className="flex justify-between items-center mt-4">
                  <span className="text-nature-600 font-medium">12 Temples</span>
                  <button className="bg-nature-600 hover:bg-nature-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
                    Explore Route
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-6">
                <h3 className="text-2xl font-bold text-nature-700 mb-3">Shakti Peethas</h3>
                <p className="text-gray-600 mb-4">
                  Sacred sites where parts of Goddess Sati's body fell after Lord Shiva carried her corpse.
                </p>
                <div className="flex justify-between items-center mt-4">
                  <span className="text-nature-600 font-medium">51 Temples</span>
                  <button className="bg-nature-600 hover:bg-nature-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
                    Explore Route
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-6">
                <h3 className="text-2xl font-bold text-nature-700 mb-3">Divya Desams</h3>
                <p className="text-gray-600 mb-4">
                  The 108 Vishnu temples glorified by the 12 Alvar saints in their Tamil hymns called Nalayira Divya
                  Prabandham.
                </p>
                <div className="flex justify-between items-center mt-4">
                  <span className="text-nature-600 font-medium">108 Temples</span>
                  <button className="bg-nature-600 hover:bg-nature-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
                    Explore Route
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Testimonials Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-orange-800 mb-4">Temple Experiences</h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Hear from devotees who have embarked on spiritual journeys to India's sacred temples
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center mr-4">
                  <span className="text-orange-800 font-bold">R</span>
                </div>
                <div>
                  <h4 className="font-bold">Rajesh Sharma</h4>
                  <p className="text-sm text-gray-500">Delhi, India</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "My journey to the Char Dham was life-changing. The spiritual energy at Kedarnath temple is something I
                will never forget. Highly recommend this pilgrimage to all devotees."
              </p>
              <div className="flex text-yellow-400 mt-4">
                <span>★</span>
                <span>★</span>
                <span>★</span>
                <span>★</span>
                <span>★</span>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center mr-4">
                  <span className="text-orange-800 font-bold">S</span>
                </div>
                <div>
                  <h4 className="font-bold">Sunita Patel</h4>
                  <p className="text-sm text-gray-500">Mumbai, India</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "The Meenakshi Temple in Madurai left me in awe. The architecture, the rituals, and the divine presence
                made it an unforgettable experience. Will definitely visit again."
              </p>
              <div className="flex text-yellow-400 mt-4">
                <span>★</span>
                <span>★</span>
                <span>★</span>
                <span>★</span>
                <span>★</span>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center mr-4">
                  <span className="text-orange-800 font-bold">M</span>
                </div>
                <div>
                  <h4 className="font-bold">Michael Brown</h4>
                  <p className="text-sm text-gray-500">London, UK</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "As a foreigner, I was welcomed warmly at every temple I visited. The Golden Temple in Amritsar was
                particularly moving with its sense of peace and community service."
              </p>
              <div className="flex text-yellow-400 mt-4">
                <span>★</span>
                <span>★</span>
                <span>★</span>
                <span>★</span>
                <span>★</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-divine-100 to-divine-200">
        <div className="container mx-auto max-w-6xl">
          <div className="bg-white rounded-xl shadow-xl overflow-hidden">
            <div className="flex flex-col md:flex-row">
              <div className="md:w-1/2 p-8 flex flex-col justify-center">
                <h3 className="text-3xl font-bold text-spiritual-700 mb-4">Stay Updated with Temple News</h3>
                <p className="text-gray-600 mb-6">
                  Subscribe to our newsletter to receive updates on temple festivals, events, and pilgrimage
                  information.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    placeholder="Your email address"
                    className="flex-grow px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-spiritual-500"
                  />
                  <button className="bg-spiritual-600 hover:bg-spiritual-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap">
                    Subscribe Now
                  </button>
                </div>
              </div>
              <div className="md:w-1/2 bg-gradient-to-br from-royal-600 to-royal-800 p-8 text-white flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-4">Download Our Temple Guide App</h3>
                <p className="mb-6">Get access to temple information, maps, and audio guides on your mobile device.</p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button className="bg-white text-royal-800 px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center">
                    <span className="mr-2">App Store</span>
                    <span>📱</span>
                  </button>
                  <button className="bg-white text-royal-800 px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center">
                    <span className="mr-2">Google Play</span>
                    <span>📱</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-spiritual-800 to-spiritual-900 text-white py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-10 h-10 bg-divine-400 rounded-full flex items-center justify-center">
                  <span className="text-spiritual-800 font-bold text-xl">D</span>
                </div>
                <span className="text-2xl font-bold">DivyaTemple</span>
              </div>
              <p className="text-spiritual-200 mb-6">
                Your comprehensive guide to exploring India's sacred temples and planning pilgrimages.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-white hover:text-divine-300 transition-colors">
                  <span className="sr-only">Facebook</span>
                  <span className="text-2xl">📱</span>
                </a>
                <a href="#" className="text-white hover:text-divine-300 transition-colors">
                  <span className="sr-only">Twitter</span>
                  <span className="text-2xl">📱</span>
                </a>
                <a href="#" className="text-white hover:text-divine-300 transition-colors">
                  <span className="sr-only">Instagram</span>
                  <span className="text-2xl">📱</span>
                </a>
                <a href="#" className="text-white hover:text-divine-300 transition-colors">
                  <span className="sr-only">YouTube</span>
                  <span className="text-2xl">📱</span>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="hover:text-divine-300 transition-colors">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/temple" className="hover:text-divine-300 transition-colors">
                    Temples
                  </Link>
                </li>
                <li>
                  <Link to="#pilgrimage" className="hover:text-divine-300 transition-colors">
                    Pilgrimage
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="hover:text-divine-300 transition-colors">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-divine-300 transition-colors">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Temple Categories</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/temple?category=Shiva" className="hover:text-divine-300 transition-colors">
                    Shiva Temples
                  </Link>
                </li>
                <li>
                  <Link to="/temple?category=Vishnu" className="hover:text-divine-300 transition-colors">
                    Vishnu Temples
                  </Link>
                </li>
                <li>
                  <Link to="/temple?category=Devi" className="hover:text-divine-300 transition-colors">
                    Devi Temples
                  </Link>
                </li>
                <li>
                  <Link to="/temple?category=Ganesha" className="hover:text-divine-300 transition-colors">
                    Ganesha Temples
                  </Link>
                </li>
                <li>
                  <Link to="/temple?category=Jyotirlinga" className="hover:text-divine-300 transition-colors">
                    Jyotirlingas
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Contact Us</h4>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="mr-2">📍</span>
                  <span>123 Temple Street, New Delhi, India</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">📞</span>
                  <span>+91 98765 43210</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">✉️</span>
                  <span>info@divyatemple.com</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-spiritual-700 mt-12 pt-6 text-center text-spiritual-300">
            <p>&copy; {new Date().getFullYear()} DivyaTemple. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
